import { useCallback, useEffect, useState } from 'react'
import Modal from 'react-modal'
import { AdminConversationDto, AdminMessageDto, ModerationContentType, UserDto } from '@ivy-monorepo/shared'
import { useNavigate } from 'react-router-dom'
import Avatar from './Avatar'
import dayjs from 'dayjs'
import ModerationActionsBar from './moderation/ModerationActionsBar'
import { adminService } from '../services/admin.service'

interface MessageBubbleProps {
  user: UserDto
  role: string
  message: AdminMessageDto
  onUserClick: (userPublicId: string) => void
  onMessageUpdated: () => void
}

const MessageBubble = ({ user, role, message, onUserClick, onMessageUpdated }: MessageBubbleProps) => {
  const isSender = role === 'Sender'
  const [isFlagged, setIsFlagged] = useState(message.isFlagged)
  const authorPublicId = message.fromUserPublicId

  useEffect(() => {
    setIsFlagged(message.isFlagged)
  }, [message.isFlagged])

  const contentBlock = (
    <>
      <div className={`message-content${isFlagged ? ' moderation-flagged-content' : ''}`}>{message.content}</div>
      <div className="message-timestamp">{dayjs(message.createdAt).format('MMM DD, YYYY [at] hh:mma')}</div>
      <ModerationActionsBar
        authorPublicId={authorPublicId}
        contentType={ModerationContentType.Message}
        contentReferenceId={String(message.id)}
        quotedContent={message.content}
        isFlagged={isFlagged}
        onFlagChange={setIsFlagged}
        onDelete={async () => {
          await adminService.deleteMessage(message.id)
          onMessageUpdated()
        }}
        deleteConfirmTitle="Delete message?"
        deleteConfirmBody="This will hide the message from users. It is retained for audit."
      />
    </>
  )

  return (
    <div className={`message-bubble ${isSender ? 'sender' : 'recipient'}`}>
      {isSender ? (
        <div className="message-layout">
          <div className="message-sender-info">
            <div onClick={() => onUserClick(user.publicId)} style={{ cursor: 'pointer' }}>
              <Avatar url={user.imageUrl} firstName={user.firstName} lastName={user.lastName} />
            </div>
            <div className="full-name" onClick={() => onUserClick(user.publicId)} style={{ cursor: 'pointer' }}>
              <div>
                {user.firstName} {user.lastName}
              </div>
            </div>
          </div>
          <div className="message-content-wrapper">{contentBlock}</div>
        </div>
      ) : (
        <div className="message-layout">
          <div className="message-content-wrapper">{contentBlock}</div>
          <div className="message-sender-info">
            <div className="full-name" onClick={() => onUserClick(user.publicId)} style={{ cursor: 'pointer' }}>
              <div>
                {user.firstName} {user.lastName}
              </div>
            </div>
            <div onClick={() => onUserClick(user.publicId)} style={{ cursor: 'pointer' }}>
              <Avatar url={user.imageUrl} firstName={user.firstName} lastName={user.lastName} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface ConversationDetailPopupProps {
  conversationPublicId: string | null
  isOpen: boolean
  onClose: () => void
}

const ConversationDetailPopup = ({ conversationPublicId, isOpen, onClose }: ConversationDetailPopupProps) => {
  const navigate = useNavigate()
  const [conversation, setConversation] = useState<AdminConversationDto | null>(null)

  const loadConversation = useCallback(async () => {
    if (!conversationPublicId) return
    try {
      const data = await adminService.getConversationDetail(conversationPublicId)
      setConversation(data)
    } catch (err) {
      console.log('Failed to load conversation', err)
    }
  }, [conversationPublicId])

  useEffect(() => {
    if (isOpen && conversationPublicId) {
      loadConversation()
    } else {
      setConversation(null)
    }
  }, [isOpen, conversationPublicId, loadConversation])

  const handleUserClick = (userPublicId: string) => {
    navigate(`/admin/users?popup=${userPublicId}`)
  }

  return (
    <Modal
      closeTimeoutMS={300}
      isOpen={isOpen}
      onRequestClose={onClose}
      className="conversation-detail-popup"
      overlayClassName="conversation-detail-overlay"
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
    >
      {conversation ? (
        <div className="popup-detail-container">
          <h2>Conversation details</h2>
          <div className="popup-messages-section">
            <div className="messages-container">
              {conversation.messages.length > 0 ? (
                conversation.messages.map((message: AdminMessageDto) => (
                  <MessageBubble
                    key={message.id}
                    role={message.role}
                    message={message}
                    user={message.fromUserPublicId === conversation.fromUser.publicId ? conversation.fromUser : conversation.toUser}
                    onUserClick={handleUserClick}
                    onMessageUpdated={loadConversation}
                  />
                ))
              ) : (
                <div className="no-messages">No messages in this conversation</div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div />
      )}
    </Modal>
  )
}

export default ConversationDetailPopup
